import { useEffect, useState, useCallback, useMemo } from 'react'
import { apiGet } from '../api'
import type { InboxResponse, InboxRow } from '../api'
import MailboxConnect from './MailboxConnect'

const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
const day = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })

function fromName(addr: string): string {
  const m = addr.match(/^"?([^"<]+?)"?\s*<.+?>$/)
  if (m && m[1]) return m[1].trim()
  return addr
}

function whenLabel(ms: number): string {
  const d = new Date(ms)
  const sameDay = new Date().toDateString() === d.toDateString()
  return sameDay ? time.format(d) : day.format(d)
}

function urgencyTint(u: number | null | undefined): { bg: string; fg: string; label: string } {
  switch (u) {
    case 5: return { bg: 'rgba(255,107,129,0.16)', fg: '#ff6b81', label: 'NOW' }
    case 4: return { bg: 'rgba(255,158,109,0.16)', fg: '#ff9e6d', label: 'TODAY' }
    case 3: return { bg: 'rgba(125,211,252,0.14)', fg: '#7dd3fc', label: 'WEEK' }
    case 2: return { bg: 'rgba(154,169,191,0.10)', fg: '#9aa9bf', label: 'LATER' }
    case 1: return { bg: 'rgba(100,116,139,0.10)', fg: '#64748b', label: 'NOISE' }
    default:return { bg: 'rgba(154,169,191,0.06)', fg: '#9aa9bf', label: '—' }
  }
}

function accountLabel(a: string | undefined): string | null {
  if (!a || a === 'gmail') return null
  if (a.startsWith('ms:')) return a.slice(3)
  if (a.startsWith('whatsapp')) return 'whatsapp'
  return a
}

function inboxLink(row: InboxRow): string {
  if (!row.account || row.account === 'gmail') {
    return `https://mail.google.com/mail/u/0/#inbox/${row.id}`
  }
  if (row.account.startsWith('whatsapp')) {
    return 'https://web.whatsapp.com/'
  }
  return 'https://outlook.office.com/mail/inbox'
}

function replyPrompt(row: InboxRow): string {
  const sender = fromName(row.from_addr)
  const body = row.snippet || row.subject || ''
  return [
    `Draft a reply to this email from ${sender}.`,
    `Subject: ${row.subject || '(no subject)'}`,
    `Their message:`,
    `> ${body.slice(0, 800)}`,
    ``,
    `My reply should be: `,
  ].join('\n')
}

function Row({ row }: { row: InboxRow }) {
  const tint = urgencyTint(row.ai_urgency)
  const acct = accountLabel(row.account)
  function reply(e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation()
    window.dispatchEvent(new CustomEvent('mercedes:open', {
      detail: { prompt: replyPrompt(row) },
    }))
  }
  return (
    <div className="inbox-row" title={row.ai_reason || ''} style={{ cursor: 'default' }}>
      <span className="urg-pill" style={{ background: tint.bg, color: tint.fg, borderColor: `${tint.fg}40` }}>
        {tint.label}
      </span>
      <a href={inboxLink(row)} target="_blank" rel="noreferrer"
         style={{ flex: 1, minWidth: 0, color: 'inherit', textDecoration: 'none' }}>
        <div className="truncate">
          <strong>{fromName(row.from_addr)}</strong>
          {' · '}
          <span style={{ color: 'var(--text-secondary)' }}>{row.subject || '(no subject)'}</span>
        </div>
        {row.ai_reason && (
          <div className="truncate" style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
            {row.ai_reason}
          </div>
        )}
      </a>
      {acct && <span className="pill" style={{ flex: '0 0 auto', fontSize: '0.6rem' }}>{acct}</span>}
      <span className="mono muted" style={{ flex: '0 0 auto', fontSize: '0.7rem' }}>{whenLabel(row.received_at)}</span>
      <button type="button" onClick={reply} title="Reply with Mercedes"
        style={{ minHeight: 22, minWidth: 22, padding: '0 6px', fontSize: '0.7rem',
                 background: 'rgba(90,242,255,0.06)', borderColor: 'rgba(90,242,255,0.28)',
                 color: 'var(--accent)' }}>
        ↩
      </button>
    </div>
  )
}

export default function InboxTriage() {
  const [data, setData] = useState<InboxResponse | null>(null)
  const [showAll, setShowAll] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await apiGet<InboxResponse>('/gmail/triage')
      setData(res)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 5 * 60_000)
    return () => clearInterval(t)
  }, [load])

  const ranked = useMemo(() => {
    if (!data) return []
    const pool = data.recent ?? data.priority ?? []
    return [...pool].sort((a, b) => {
      const au = a.ai_urgency ?? 0
      const bu = b.ai_urgency ?? 0
      if (bu !== au) return bu - au
      return b.received_at - a.received_at
    })
  }, [data])

  const hot = data?.hot ?? ranked.filter(r => (r.ai_urgency ?? 0) >= 4)
  const visible = showAll ? ranked : ranked.slice(0, 6)

  if (!data) return (
    <section className="panel work-panel">
      <div className="panel-header"><span>Inbox</span></div>
      <div className="empty-state">Loading…</div>
    </section>
  )

  return (
    <section className="panel work-panel">
      <div className="panel-header">
        <span>Inbox · AI ranked</span>
        <span className="mono muted">{hot.length} hot · {data.total_unread} unread</span>
      </div>
      <MailboxConnect />
      {ranked.length === 0 && <div className="empty-state">Nothing new.</div>}
      <div className="inbox-list">
        {visible.map(r => <Row key={r.id} row={r} />)}
      </div>
      {ranked.length > 6 && (
        <button type="button" onClick={() => setShowAll(v => !v)}
          style={{ marginTop: 6, width: '100%', minHeight: 28, fontSize: '0.7rem' }}>
          {showAll ? 'collapse' : `show ${ranked.length - 6} more`}
        </button>
      )}
    </section>
  )
}
